import * as React from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { CurrencySelector } from '@/components/ui/CurrencySelector';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { BudgetTreePicker } from '@/components/budget/BudgetTreePicker';
import { useFxRate, useFxRateUtils } from '@/hooks/useFxRates';
import { formatCurrency, formatDateForInput } from '@/lib/utils';
import type { Budget, CreateBudgetRequest, UpdateBudgetRequest, BudgetType } from '@/types';

interface BudgetFormProps {
  budget?: Budget;
  parentBudgets?: Budget[];
  initialParentId?: string;
  initialStartDate?: string;
  initialEndDate?: string;
  onSubmit: (data: CreateBudgetRequest | UpdateBudgetRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

interface FormData {
  name: string;
  type: BudgetType;
  parentId: string;
  currency: string;
  amount: string;
  startDate: string;
  endDate: string;
  rolloverEnabled: boolean;
  rolloverAmount: string;
  autoClassificationEnabled: boolean;
}

export function BudgetForm({
  budget,
  parentBudgets = [],
  initialParentId,
  initialStartDate,
  initialEndDate,
  onSubmit,
  onCancel,
  isLoading,
}: BudgetFormProps) {
  const isEdit = !!budget;
  const isNonOwnerEdit = isEdit && budget.owned === false;
  const { getCommonCurrencies } = useFxRateUtils();
  
  const defaultStartDate = budget?.startDate 
    ? formatDateForInput(budget.startDate)
    : initialStartDate
      ? formatDateForInput(initialStartDate)
      : formatDateForInput(new Date());
  
  const defaultEndDate = budget?.endDate
    ? formatDateForInput(budget.endDate)
    : initialEndDate
      ? formatDateForInput(initialEndDate)
      : formatDateForInput(new Date(new Date().setMonth(new Date().getMonth() + 1)));

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      name: budget?.name ?? '',
      type: budget?.type ?? 'DISCRETIONARY',
      parentId: budget?.parentId ?? initialParentId ?? '',
      currency:
        budget?.currency ??
        parentBudgets.find((p) => p.id === (initialParentId ?? ''))?.currency ??
        'USD',
      amount: budget?.amount?.toString() ?? '',
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      rolloverEnabled: budget?.rolloverEnabled ?? true,
      rolloverAmount: budget?.rolloverAmount?.toString() ?? '',
      autoClassificationEnabled: budget?.autoClassificationEnabled ?? false,
    },
  });

  const currencies = getCommonCurrencies();
  
  const typeOptions = [
    { value: 'OBLIGATION', label: 'Obligation - Required payment' },
    { value: 'DISCRETIONARY', label: 'Discretionary - Track against a limit' },
  ];

  const parentId = watch('parentId');
  const currency = watch('currency');
  const amountValue = watch('amount');
  const rolloverEnabled = watch('rolloverEnabled');

  const selectedParent = React.useMemo(
    () => parentBudgets.find((p) => p.id === parentId),
    [parentBudgets, parentId]
  );

  const availableForChild = React.useMemo(() => {
    if (!selectedParent) return undefined;
    if (selectedParent.unallocated !== undefined) {
      return Math.max(selectedParent.unallocated, 0);
    }

    const directChildren = selectedParent.children ?? [];
    const directAllocatedSameCurrency = directChildren.reduce((total, child) => {
      if (child.currency !== selectedParent.currency) return total;
      return total + child.amount;
    }, 0);

    return Math.max(selectedParent.amount - directAllocatedSameCurrency, 0);
  }, [selectedParent]);

  const { data: parentFxRate, isLoading: parentFxLoading } = useFxRate(
    currency ?? '',
    selectedParent?.currency ?? ''
  );

  const { data: existingBudgetToParentFxRate, isLoading: existingBudgetToParentFxLoading } = useFxRate(
    isEdit && selectedParent && budget && selectedParent.currency !== budget.currency
      ? budget.currency
      : '',
    isEdit && selectedParent && budget && selectedParent.currency !== budget.currency
      ? selectedParent.currency
      : ''
  );

  const isEditingParentWithChildren = isEdit && !!budget && (budget.children?.length ?? 0) > 0;

  const { data: editedAmountToOriginalParentCurrencyRate, isLoading: editedAmountToOriginalParentCurrencyLoading } = useFxRate(
    isEditingParentWithChildren && budget && currency !== budget.currency
      ? currency
      : '',
    isEditingParentWithChildren && budget && currency !== budget.currency
      ? budget.currency
      : ''
  );

  const minParentAmountInOriginalCurrency = React.useMemo(() => {
    if (!isEditingParentWithChildren || !budget) return null;

    const availableForParentSelf =
      budget.unallocated !== undefined
        ? Math.max(budget.unallocated, 0)
        : Math.max(
            budget.amount -
              (budget.children ?? []).reduce((total, child) => {
                if (child.currency !== budget.currency) return total;
                return total + child.amount;
              }, 0),
            0
          );

    return Math.max(0, budget.amount - availableForParentSelf);
  }, [budget, isEditingParentWithChildren]);

  const minParentAmountInFormCurrency = React.useMemo(() => {
    if (!isEditingParentWithChildren || !budget || minParentAmountInOriginalCurrency === null) {
      return null;
    }

    if (currency === budget.currency) {
      return minParentAmountInOriginalCurrency;
    }

    if (!editedAmountToOriginalParentCurrencyRate || editedAmountToOriginalParentCurrencyRate <= 0) {
      return null;
    }

    return minParentAmountInOriginalCurrency / editedAmountToOriginalParentCurrencyRate;
  }, [
    budget,
    currency,
    editedAmountToOriginalParentCurrencyRate,
    isEditingParentWithChildren,
    minParentAmountInOriginalCurrency,
  ]);

  const availableForThisChild = React.useMemo(() => {
    if (!selectedParent || availableForChild === undefined) return availableForChild;
    if (!isEdit || !budget || budget.parentId !== selectedParent.id) {
      return availableForChild;
    }

    if (selectedParent.currency === budget.currency) {
      return availableForChild + budget.amount;
    }

    if (!existingBudgetToParentFxRate) {
      return undefined;
    }

    return availableForChild + budget.amount * existingBudgetToParentFxRate;
  }, [
    availableForChild,
    budget,
    existingBudgetToParentFxRate,
    isEdit,
    selectedParent,
  ]);

  const childAmountInParent = React.useMemo(() => {
    if (!selectedParent) return null;
    if (selectedParent.currency === currency) return null;
    const parsed = parseFloat(amountValue || '');
    if (Number.isNaN(parsed) || !parentFxRate) return null;
    return parsed * parentFxRate;
  }, [amountValue, currency, parentFxRate, selectedParent]);


  const handleFormSubmit = async (data: FormData) => {
    // If this is a child budget, inherit dates from parent
    let startDate = data.startDate;
    let endDate = data.endDate;
    
    if (data.parentId) {
      const parent = parentBudgets.find(p => p.id === data.parentId);
      if (parent) {
        startDate = formatDateForInput(parent.startDate);
        endDate = formatDateForInput(parent.endDate);
      }
    }
    
    const request = {
      name: data.name,
      type: data.type,
      parentId: data.parentId || undefined,
      currency: data.currency,
      amount: parseFloat(data.amount),
      startDate: startDate,
      endDate: endDate,
      rolloverEnabled: data.rolloverEnabled,
      rolloverAmount:
        data.rolloverEnabled && data.rolloverAmount
          ? parseFloat(data.rolloverAmount)
          : undefined,
      ...(!data.parentId && { autoClassificationEnabled: data.autoClassificationEnabled }),
      ...(isEdit && { version: budget.version }),
    };
    
    await onSubmit(request as CreateBudgetRequest | UpdateBudgetRequest);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{isEdit ? 'Edit Budget' : 'Create Budget'}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <Input
            label="Budget Name"
            placeholder="e.g., Groceries, Rent, Entertainment"
            error={errors.name?.message}
            {...register('name', { required: 'Name is required' })}
          />

          <Select
            label="Budget Type"
            options={typeOptions}
            error={errors.type?.message}
            {...register('type')}
          />

          {parentBudgets.length > 0 && (
            <BudgetTreePicker
              budgets={parentBudgets}
              value={parentId}
              onChange={(id) => setValue('parentId', id, { shouldValidate: true })}
              excludeId={budget?.id}
              label="Parent Budget (optional)"
              helperText="Nest this budget under another budget"
            />
          )}

          {/* Amount with currency selector */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Amount and Currency
            </label>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  {...register('amount', {
                    required: 'Amount is required',
                    min: { value: 0, message: 'Amount must be positive' },
                    validate: (value) => {
                      const parsed = parseFloat(value);
                      if (Number.isNaN(parsed)) {
                        return 'Amount must be a number';
                      }

                      if (isEditingParentWithChildren) {
                        if (minParentAmountInFormCurrency === null) {
                          return 'Waiting for FX rate to validate against child allocations';
                        }
                        if (parsed + 1e-6 < minParentAmountInFormCurrency) {
                          return `Parent budget cannot be less than children allocations (${formatCurrency(minParentAmountInFormCurrency, currency)})`;
                        }
                      }

                      if (selectedParent && availableForThisChild !== undefined) {
                        if (selectedParent.currency !== currency) {
                          if (!parentFxRate) {
                            return 'Waiting for FX rate to validate against parent';
                          }
                          const converted = parsed * parentFxRate;
                          if (converted > availableForThisChild + 1e-6) {
                            return `Exceeds parent's available allocation (${formatCurrency(availableForThisChild, selectedParent.currency)})`;
                          }
                        } else if (parsed > availableForThisChild + 1e-6) {
                          return `Exceeds parent's available allocation (${formatCurrency(availableForThisChild, selectedParent.currency)})`;
                        }
                      } else if (selectedParent && availableForThisChild === undefined) {
                        return 'Waiting for FX rate to validate against parent';
                      }
                      return true;
                    },
                  })}
                />
              </div>
              <CurrencySelector
                value={currency}
                onChange={(newCurrency) => setValue('currency', newCurrency)}
                currencies={currencies}
              />
            </div>
            {errors.amount && (
              <p className="text-sm text-destructive mt-1.5">{errors.amount.message}</p>
            )}
            {selectedParent && (
              <p className="text-sm text-muted-foreground mt-1.5">
                Available in {selectedParent.name}: {formatCurrency(availableForThisChild ?? 0, selectedParent.currency)}
                {isEdit && budget?.parentId === selectedParent.id && (
                  <span> (includes current allocation)</span>
                )}
                {selectedParent.currency !== currency && (
                  <> | Your amount ≈ {childAmountInParent !== null ? formatCurrency(childAmountInParent, selectedParent.currency) : parentFxLoading || existingBudgetToParentFxLoading ? 'Fetching FX rate...' : '--'} ({selectedParent.currency})</>
                )}
              </p>
            )}
            {!selectedParent && isEditingParentWithChildren && (
              <p className="text-sm text-muted-foreground mt-1.5">
                Minimum allowed for this parent: {minParentAmountInFormCurrency !== null
                  ? formatCurrency(minParentAmountInFormCurrency, currency)
                  : editedAmountToOriginalParentCurrencyLoading
                    ? 'Fetching FX rate...'
                    : '--'}
              </p>
            )}
          </div>

          {!parentId && (
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Start Date"
                type="date"
                error={errors.startDate?.message}
                {...register('startDate', { required: 'Start date is required' })}
              />
              <Input
                label="End Date"
                type="date"
                error={errors.endDate?.message}
                {...register('endDate', { required: 'End date is required' })}
              />
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="rolloverEnabled"
              className="h-4 w-4 rounded border-gray-300"
              {...register('rolloverEnabled')}
            />
            <label htmlFor="rolloverEnabled" className="text-sm text-foreground">
              Enable rollover (carry unused amount to next period)
            </label>
          </div>

          {rolloverEnabled && (
            <Input
              label="Rollover Amount"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              error={errors.rolloverAmount?.message}
              {...register('rolloverAmount', {
                validate: (value) => {
                  if (!value) return true;
                  const parsed = parseFloat(value);
                  if (Number.isNaN(parsed)) return 'Rollover amount must be a number';
                  if (parsed < 0) return 'Rollover amount must be positive';
                  return true;
                },
              })}
            />
          )}

          {!parentId && (
            <div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="autoClassificationEnabled"
                  className="h-4 w-4 rounded border-gray-300"
                  disabled={isNonOwnerEdit}
                  {...register('autoClassificationEnabled')}
                />
                <label htmlFor="autoClassificationEnabled" className="text-sm text-foreground">
                  Auto-classify spending by merchant
                </label>
              </div>
              <p className="text-xs text-muted-foreground mt-1 ml-6">
                {isNonOwnerEdit
                  ? 'Only the budget owner can change this.'
                  : 'When enabled, new API-imported transactions are placed in a child budget based on merchant history and feedback.'}
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" loading={isLoading} className="flex-1">
              {isEdit ? 'Save Changes' : 'Create Budget'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
